import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT_MS = 20000;

// Der Maps-Key wird serverseitig zur Laufzeit übergeben (includes/MapsRuntime.php).
function mapsKey() {
  const ds = document.documentElement.dataset;
  return ds && typeof ds.mapsKey === "string" ? ds.mapsKey.trim() : "";
}

function finiteNumber(value) {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && isFinite(n) ? n : null;
}

function text(value) {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.pendingSource = null;
    this.requestId = 0;
  }

  // Beendet eine laufende Suche; ihre Antwort wird danach ignoriert.
  cancelAutocomplete() {
    this.requestId++;
    if (this.pendingSource != null) {
      this.pendingSource.cancel();
      this.pendingSource = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature in das bisherige Adressobjekt um, ohne das Feature zu verändern.
  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // Koordinaten kommen als [lng, lat].
    const lng = finiteNumber(l.geometry.coordinates[0]);
    const lat = finiteNumber(l.geometry.coordinates[1]);
    if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const props = l.properties;
    const type = text(props.type);
    const name = text(props.name);
    const geoaddress = {};

    let street = text(props.street);
    let city = text(props.city);
    let district = text(props.district);
    const zip = text(props.postcode);
    const number = text(props.housenumber);

    if (type) {
      switch (type) {
        case "house":
          break;
        case "street":
          street = street || name;
          break;
        case "district":
          district = district || (name !== city ? name : null);
          break;
        case "city":
          city = city || name;
          break;
        default:
          // locality und andere Typen: nur vorhandene Felder übernehmen.
          break;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type.
      if (props.osm_key == "highway") {
        street = street || name;
      } else if (!city && name) {
        city = name;
      } else if (!district && name && name !== city) {
        if (props.osm_key == "boundary" || (props.osm_key == "place" && props.osm_value == "suburb")) {
          district = name;
        }
      }
    }

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    if (zip) {
      geoaddress["zip"] = zip;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = props.state;
    geoaddress["country"] = props.country;

    // Ohne Orts- und Straßenangabe bleibt der Feature-Name die Beschriftung.
    geoaddress["text"] = this._geoaddressText(geoaddress) || name || "";

    return geoaddress;
  }

  _features(res) {
    return res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
  }

  // Liefert die Treffer der jüngsten Suche; überholte oder abgebrochene Suchen liefern null.
  autocomplete(q) {
    this.cancelAutocomplete();

    const key = mapsKey();
    const query = typeof q === "string" ? q.trim() : "";
    if (!query || !key) {
      return Promise.resolve(key ? [] : null);
    }

    const source = this.axios.CancelToken.source();
    const requestId = this.requestId;
    this.pendingSource = source;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    const done = () => {
      if (this.pendingSource === source) {
        this.pendingSource = null;
      }
    };

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      done();
      if (requestId !== this.requestId) {
        return null;
      }

      const results = [];
      this._features(res).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }, thrown => {
      done();
      if (this.axios.isCancel(thrown) || requestId !== this.requestId) {
        return null;
      }
      throw thrown;
    });
  }

  reverse(lat, lng) {
    const key = mapsKey();
    const nLat = finiteNumber(lat);
    const nLng = finiteNumber(lng);

    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }
    if (nLat === null || nLng === null || nLat < -90 || nLat > 90 || nLng < -180 || nLng > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const params = new URLSearchParams();
    params.append("lat", nLat);
    params.append("lon", nLng);
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      const features = this._features(res);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }
      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
